import React, { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { 
  Settings, 
  Users, 
  Tag, 
  FileText, 
  BarChart3, 
  Bot,
  CheckCircle,
  XCircle,
  RefreshCw
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { Badge } from '@/components/ui/Badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/Tabs';
import { WhatsAppIntegrationService, WhatsAppConfig, MessageTemplate, Contact, WhatsAppTag, WhatsAppAttendant } from '@/services/whatsappIntegrationService';
// import { NotificationService } from '@/services/notificationService';

const WhatsAppIntegrationPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('config');
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [showTagModal, setShowTagModal] = useState(false);
  const [showUserModal, setShowUserModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  // Estados para formulários
  const [configForm, setConfigForm] = useState<Partial<WhatsAppConfig>>({});
  const [templateForm, setTemplateForm] = useState<Partial<MessageTemplate>>({});
  const [templateVars, setTemplateVars] = useState<Record<string, string>>({});
  const [templatePreview, setTemplatePreview] = useState('');
  const [templatePreviewLoading, setTemplatePreviewLoading] = useState(false);
  const [templateTestPhone, setTemplateTestPhone] = useState('');
  const [contactForm, setContactForm] = useState<Partial<Contact>>({});
  const [tagForm, setTagForm] = useState<Partial<WhatsAppTag>>({});
  const [userForm, setUserForm] = useState<Partial<WhatsAppAttendant>>({});

  // Queries
  const { data: config } = useQuery({
    queryKey: ['whatsapp-config'],
    queryFn: WhatsAppIntegrationService.getConfig,
  });

  const { data: status, isLoading: loadingStatus } = useQuery({
    queryKey: ['whatsapp-status'],
    queryFn: WhatsAppIntegrationService.getInstanceStatus,
    refetchInterval: 30000, // Atualizar a cada 30 segundos
  });

  const { data: templates, isLoading: loadingTemplates } = useQuery({
    queryKey: ['whatsapp-templates'],
    queryFn: WhatsAppIntegrationService.getTemplates,
  });

  const { data: contacts, isLoading: loadingContacts } = useQuery({
    queryKey: ['whatsapp-contacts'],
    queryFn: WhatsAppIntegrationService.getContacts,
  });

  const { data: tags, isLoading: loadingTags } = useQuery({
    queryKey: ['whatsapp-tags'],
    queryFn: WhatsAppIntegrationService.getTags,
  });

  const { data: users, isLoading: loadingUsers } = useQuery({
    queryKey: ['whatsapp-users'],
    queryFn: WhatsAppIntegrationService.getUsers,
  });

  const { data: statistics, isLoading: loadingStatistics } = useQuery({
    queryKey: ['whatsapp-statistics'],
    queryFn: () => WhatsAppIntegrationService.getStatistics('7d'),
  });

  // Handlers
  const handleSaveConfig = async () => {
    try {
      setLoading(true);
      await WhatsAppIntegrationService.updateConfig(configForm);
      await queryClient.invalidateQueries({ queryKey: ['whatsapp-config'] });
      setShowConfigModal(false);
      console.log('Configuração salva com sucesso!');
    } catch (error) {
      console.error('Erro ao salvar configuração');
    } finally {
      setLoading(false);
    }
  };

  const handleTestConnection = async () => {
    try {
      setLoading(true);
      await WhatsAppIntegrationService.testConnection();
      console.log('Conexão testada com sucesso!');
    } catch (error) {
      console.error('Erro ao testar conexão');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveTemplate = async () => {
    try {
      setLoading(true);
      if (selectedItem) {
        await WhatsAppIntegrationService.updateTemplate(selectedItem.id, templateForm);
      } else {
        await WhatsAppIntegrationService.createTemplate(templateForm as Omit<MessageTemplate, 'id'>);
      }
      await queryClient.invalidateQueries({ queryKey: ['whatsapp-templates'] });
      setShowTemplateModal(false);
      setSelectedItem(null);
      setTemplateForm({});
      setTemplateVars({});
      setTemplatePreview('');
      console.log('Template salvo com sucesso!');
    } catch (error) {
      console.error('Erro ao salvar template');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveContact = async () => {
    try {
      setLoading(true);
      if (selectedItem) {
        await WhatsAppIntegrationService.updateContact(selectedItem.id, contactForm);
      } else {
        await WhatsAppIntegrationService.createContact(contactForm as Omit<Contact, 'id'>);
      }
      await queryClient.invalidateQueries({ queryKey: ['whatsapp-contacts'] });
      setShowContactModal(false);
      setSelectedItem(null);
      setContactForm({});
      console.log('Contato salvo com sucesso!');
    } catch (error) {
      console.error('Erro ao salvar contato');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveTag = async () => {
    try {
      setLoading(true);
      if (selectedItem) {
        await WhatsAppIntegrationService.updateTag(selectedItem.id, tagForm);
      } else {
        await WhatsAppIntegrationService.createTag(tagForm as Omit<WhatsAppTag, 'id'>);
      }
      await queryClient.invalidateQueries({ queryKey: ['whatsapp-tags'] });
      setShowTagModal(false);
      setSelectedItem(null);
      setTagForm({});
      console.log('Etiqueta salva com sucesso!');
    } catch (error) {
      console.error('Erro ao salvar etiqueta');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveUser = async () => {
    try {
      setLoading(true);
      if (selectedItem) {
        await WhatsAppIntegrationService.updateUser(selectedItem.id, userForm);
      } else {
        await WhatsAppIntegrationService.createUser(userForm as Omit<WhatsAppAttendant, 'id'>);
      }
      await queryClient.invalidateQueries({ queryKey: ['whatsapp-users'] });
      setShowUserModal(false);
      setSelectedItem(null);
      setUserForm({});
      console.log('Usuário salvo com sucesso!');
    } catch (error) {
      console.error('Erro ao salvar usuário');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (type: string, id: string) => {
    try {
      setLoading(true);
      switch (type) {
        case 'template':
          await WhatsAppIntegrationService.deleteTemplate(id);
          await queryClient.invalidateQueries({ queryKey: ['whatsapp-templates'] });
          break;
        case 'contact':
          await WhatsAppIntegrationService.deleteContact(id);
          await queryClient.invalidateQueries({ queryKey: ['whatsapp-contacts'] });
          break;
        case 'tag':
          await WhatsAppIntegrationService.deleteTag(id);
          await queryClient.invalidateQueries({ queryKey: ['whatsapp-tags'] });
          break;
        case 'user':
          await WhatsAppIntegrationService.deleteUser(id);
          await queryClient.invalidateQueries({ queryKey: ['whatsapp-users'] });
          break;
      }
      console.log('Item excluído com sucesso!');
    } catch (error) {
      console.error('Erro ao excluir item');
    } finally {
      setLoading(false);
    }
  };

  const openModal = (type: string, item?: any) => {
    setSelectedItem(item);
    switch (type) {
      case 'config':
        setConfigForm(config || {});
        setShowConfigModal(true);
        break;
      case 'template':
        setTemplateForm(item || {});
        setTemplatePreview('');
        setTemplateVars(() => {
          const v: Record<string, string> = {};
          if (Array.isArray(item?.variables)) {
            item.variables.forEach((k: string) => { v[k] = templateVars[k] || ''; });
          }
          return v;
        });
        setShowTemplateModal(true);
        break;
      case 'contact':
        setContactForm(item || {});
        setShowContactModal(true);
        break;
      case 'tag':
        setTagForm(item || {});
        setShowTagModal(true);
        break;
      case 'user':
        setUserForm(item || {});
        setShowUserModal(true);
        break;
    }
  };

  const parseVariablesFromContent = (content: string): string[] => {
    const vars = new Set<string>();
    const regex = /\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g;
    let match: RegExpExecArray | null;
    while ((match = regex.exec(content)) !== null) {
      vars.add(match[1]);
    }
    return Array.from(vars);
  };

  const detectTemplateVariables = () => {
    const content = templateForm.content || '';
    const vars = parseVariablesFromContent(content);
    const vmap: Record<string, string> = {};
    vars.forEach((k) => { vmap[k] = templateVars[k] || ''; });
    setTemplateVars(vmap);
    setTemplateForm((prev) => ({ ...prev, variables: vars } as any));
  };

  const canPreview = useMemo(() => !!selectedItem?.id, [selectedItem]);

  const handleGeneratePreview = async () => {
    try {
      setTemplatePreviewLoading(true);
      if (selectedItem?.id) {
        const res = await WhatsAppIntegrationService.previewTemplate(selectedItem.id, templateVars);
        setTemplatePreview(res.preview || '');
      } else {
        // Preview local usando process-variables do backend
        const res = await WhatsAppIntegrationService.processVariables(templateForm.content || '', templateVars);
        setTemplatePreview(res || '');
      }
    } catch (e) {
      setTemplatePreview('');
    } finally {
      setTemplatePreviewLoading(false);
    }
  };

  const handleSendTemplateTest = async () => {
    if (!templateTestPhone.trim()) return;
    if (!selectedItem?.id) return;
    try {
      setLoading(true);
      await WhatsAppIntegrationService.sendTemplateMessage(templateTestPhone.trim(), selectedItem.id, templateVars);
      // NotificationService.success('Mensagem de teste enviada');
    } catch (e) {
      // NotificationService.error('Erro ao enviar teste');
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = () => {
    if (loadingStatus) return <RefreshCw className="w-4 h-4 animate-spin" />;
    if (status?.connected) return <CheckCircle className="w-4 h-4 text-green-500" />;
    return <XCircle className="w-4 h-4 text-red-500" />;
  };

  const getStatusText = () => {
    if (loadingStatus) return 'Verificando...';
    if (status?.connected) return 'Conectado';
    return 'Desconectado';
  };

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">Integração WhatsApp</h1>
        <div className="flex items-center gap-2">
          {getStatusIcon()}
          <span className="text-sm">{getStatusText()}</span>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-7">
          <TabsTrigger value="config" className="flex items-center gap-2">
            <Settings className="w-4 h-4" />
            Configuração
          </TabsTrigger>
          <TabsTrigger value="templates" className="flex items-center gap-2">
            <FileText className="w-4 h-4" />
            Templates
          </TabsTrigger>
          <TabsTrigger value="contacts" className="flex items-center gap-2">
            <Users className="w-4 h-4" />
            Contatos
          </TabsTrigger>
          <TabsTrigger value="tags" className="flex items-center gap-2">
            <Tag className="w-4 h-4" />
            Etiquetas
          </TabsTrigger>
          <TabsTrigger value="users" className="flex items-center gap-2">
            <Users className="w-4 h-4" />
            Atendentes
          </TabsTrigger>
          <TabsTrigger value="chatbot" className="flex items-center gap-2">
            <Bot className="w-4 h-4" />
            Chatbot
          </TabsTrigger>
          <TabsTrigger value="statistics" className="flex items-center gap-2">
            <BarChart3 className="w-4 h-4" />
            Estatísticas
          </TabsTrigger>
        </TabsList>

        <TabsContent value="config" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                Configuração da API
                <Button onClick={() => openModal('config')}>
                  <Settings className="w-4 h-4 mr-2" />
                  Editar
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">URL da API</label>
                  <p className="text-sm text-muted-foreground">{config?.baseUrl || 'Não configurado'}</p>
                </div>
                <div>
                  <label className="text-sm font-medium">API ID</label>
                  <p className="text-sm text-muted-foreground">{config?.apiId || 'Não configurado'}</p>
                </div>
                <div>
                  <label className="text-sm font-medium">Webhook URL</label>
                  <p className="text-sm text-muted-foreground">{config?.webhookUrl || 'Não configurado'}</p>
                </div>
                <div>
                  <label className="text-sm font-medium">Status</label>
                  <div className="flex items-center gap-2">
                    {getStatusIcon()}
                    <span className="text-sm">{getStatusText()}</span>
                  </div>
                </div>
              </div>
              <div className="mt-4">
                <Button onClick={handleTestConnection} disabled={loading}>
                  <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                  Testar Conexão
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="templates" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                Templates de Mensagem
                <Button onClick={() => openModal('template')}>
                  <FileText className="w-4 h-4 mr-2" />
                  Novo Template
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loadingTemplates ? (
                <div className="text-center py-8">Carregando...</div>
              ) : templates?.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  Nenhum template encontrado
                </div>
              ) : (
                <div className="grid gap-4">
                  {templates?.map((template) => (
                    <div key={template.id} className="border rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="font-medium">{template.name}</h3>
                          <p className="text-sm text-muted-foreground">{template.content}</p>
                          <div className="flex gap-2 mt-2">
                            <Badge variant="outline">{template.category}</Badge>
                            {template.isActive && <Badge variant="default">Ativo</Badge>}
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openModal('template', template)}
                          >
                            Editar
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDelete('template', template.id)}
                          >
                            Excluir
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="contacts" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                Contatos
                <Button onClick={() => openModal('contact')}>
                  <Users className="w-4 h-4 mr-2" />
                  Novo Contato
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loadingContacts ? (
                <div className="text-center py-8">Carregando...</div>
              ) : contacts?.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  Nenhum contato encontrado
                </div>
              ) : (
                <div className="grid gap-4">
                  {contacts?.map((contact) => (
                    <div key={contact.id} className="border rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="font-medium">{contact.name}</h3>
                          <p className="text-sm text-muted-foreground">{contact.phone}</p>
                          {contact.email && (
                            <p className="text-sm text-muted-foreground">{contact.email}</p>
                          )}
                          <div className="flex gap-2 mt-2">
                            {contact.tags?.map((tag) => (
                              <Badge key={tag} variant="outline">{tag}</Badge>
                            ))}
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openModal('contact', contact)}
                          >
                            Editar
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDelete('contact', contact.id)}
                          >
                            Excluir
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="tags" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                Etiquetas
                <Button onClick={() => openModal('tag')}>
                  <Tag className="w-4 h-4 mr-2" />
                  Nova Etiqueta
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loadingTags ? (
                <div className="text-center py-8">Carregando...</div>
              ) : tags?.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  Nenhuma etiqueta encontrada
                </div>
              ) : (
                <div className="grid gap-4">
                  {tags?.map((tag) => (
                    <div key={tag.id} className="border rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div
                            className="w-4 h-4 rounded-full"
                            style={{ backgroundColor: tag.color }}
                          />
                          <div>
                            <h3 className="font-medium">{tag.name}</h3>
                            {tag.description && (
                              <p className="text-sm text-muted-foreground">{tag.description}</p>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openModal('tag', tag)}
                          >
                            Editar
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDelete('tag', tag.id)}
                          >
                            Excluir
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="users" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                Atendentes
                <Button onClick={() => openModal('user')}>
                  <Users className="w-4 h-4 mr-2" />
                  Novo Atendente
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loadingUsers ? (
                <div className="text-center py-8">Carregando...</div>
              ) : users?.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  Nenhum atendente encontrado
                </div>
              ) : (
                <div className="grid gap-4">
                  {users?.map((user) => (
                    <div key={user.id} className="border rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="font-medium">{user.name}</h3>
                          <p className="text-sm text-muted-foreground">{user.email}</p>
                          {user.phone && (
                            <p className="text-sm text-muted-foreground">{user.phone}</p>
                          )}
                          <div className="flex gap-2 mt-2">
                            <Badge variant="outline">{user.role}</Badge>
                            {user.isActive && <Badge variant="default">Ativo</Badge>}
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openModal('user', user)}
                          >
                            Editar
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDelete('user', user.id)}
                          >
                            Excluir
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="chatbot" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bot className="w-4 h-4" />
                Configuração do Chatbot
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-medium">Chatbot IA Nativa</h3>
                    <p className="text-sm text-muted-foreground">
                      Ative o chatbot para respostas automáticas
                    </p>
                  </div>
                  <Button variant="outline">
                    <Bot className="w-4 h-4 mr-2" />
                    Configurar
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="statistics" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="w-4 h-4" />
                Estatísticas dos Últimos 7 Dias
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loadingStatistics ? (
                <div className="text-center py-8">Carregando...</div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="text-center p-4 border rounded-lg">
                    <h3 className="text-2xl font-bold text-blue-600">
                      {statistics?.messagesSent || 0}
                    </h3>
                    <p className="text-sm text-muted-foreground">Mensagens Enviadas</p>
                  </div>
                  <div className="text-center p-4 border rounded-lg">
                    <h3 className="text-2xl font-bold text-green-600">
                      {statistics?.messagesReceived || 0}
                    </h3>
                    <p className="text-sm text-muted-foreground">Mensagens Recebidas</p>
                  </div>
                  <div className="text-center p-4 border rounded-lg">
                    <h3 className="text-2xl font-bold text-purple-600">
                      {statistics?.activeContacts || 0}
                    </h3>
                    <p className="text-sm text-muted-foreground">Contatos Ativos</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Modal de Configuração */}
      <Modal
        isOpen={showConfigModal}
        onClose={() => setShowConfigModal(false)}
        title="Configuração da API GestorLeads"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              URL da API
            </label>
            <Input
              value={configForm.baseUrl || ''}
              onChange={(e) => setConfigForm({ ...configForm, baseUrl: e.target.value })}
              placeholder="https://enterprise-207api.gestorleads.com.br"
            />
            <p className="text-xs text-muted-foreground mt-1">
              URL base da API do GestorLeads
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              API ID
            </label>
            <Input
              value={configForm.apiId || ''}
              onChange={(e) => setConfigForm({ ...configForm, apiId: e.target.value })}
              placeholder="ID da API GestorLeads"
            />
            <p className="text-xs text-muted-foreground mt-1">
              ID da API usado nos endpoints do GestorLeads
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              API Token
            </label>
            <Input
              type="password"
              value={configForm.apiToken || ''}
              onChange={(e) => setConfigForm({ ...configForm, apiToken: e.target.value })}
              placeholder="Bearer Token de autenticação"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Token de autenticação (Bearer Token) fornecido pelo GestorLeads
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              Webhook URL <span className="text-gray-400">(Opcional)</span>
            </label>
            <Input
              value={configForm.webhookUrl || ''}
              onChange={(e) => setConfigForm({ ...configForm, webhookUrl: e.target.value })}
              placeholder="https://seu-dominio.com/webhook"
            />
            <p className="text-xs text-muted-foreground mt-1">
              URL pública para receber notificações do GestorLeads
            </p>
          </div>
          <div className="flex gap-2">
            <Button onClick={handleSaveConfig} disabled={loading}>
              {loading ? 'Salvando...' : 'Salvar'}
            </Button>
            <Button variant="outline" onClick={() => setShowConfigModal(false)}>
              Cancelar
            </Button>
          </div>
        </div>
      </Modal>

      {/* Modal de Template */}
      <Modal
        isOpen={showTemplateModal}
        onClose={() => setShowTemplateModal(false)}
        title={selectedItem ? 'Editar Template' : 'Novo Template'}
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Nome</label>
            <Input
              value={templateForm.name || ''}
              onChange={(e) => setTemplateForm({ ...templateForm, name: e.target.value })}
              placeholder="Nome do template"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Categoria</label>
            <Input
              value={templateForm.category || ''}
              onChange={(e) => setTemplateForm({ ...templateForm, category: e.target.value })}
              placeholder="cobranca, marketing, suporte"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Conteúdo</label>
            <textarea
              className="w-full p-2 border rounded-md"
              rows={4}
              value={templateForm.content || ''}
              onChange={(e) => setTemplateForm({ ...templateForm, content: e.target.value })}
              placeholder="Conteúdo da mensagem com variáveis {{nome}}, {{valor}}, etc."
            />
            <div className="flex gap-2 mt-2">
              <Button variant="outline" size="sm" onClick={detectTemplateVariables}>Detectar Variáveis</Button>
              <Button variant="outline" size="sm" onClick={handleGeneratePreview} disabled={templatePreviewLoading}>
                {templatePreviewLoading ? 'Gerando...' : 'Gerar Preview'}
              </Button>
            </div>
          </div>
          {Object.keys(templateVars).length > 0 && (
            <div>
              <div className="text-xs text-muted-foreground mb-1">Variáveis</div>
              <div className="grid md:grid-cols-3 gap-2">
                {Object.keys(templateVars).map((k) => (
                  <div key={k}>
                    <label className="block text-[11px] text-muted-foreground mb-1">{k}</label>
                    <Input value={templateVars[k] || ''} onChange={(e) => setTemplateVars(prev => ({ ...prev, [k]: e.target.value }))} placeholder={`Valor para {{${k}}}`} />
                  </div>
                ))}
              </div>
            </div>
          )}
          <div>
            <div className="text-xs text-muted-foreground mb-1">Preview</div>
            <div className="min-h-[80px] whitespace-pre-wrap p-2 bg-muted/50 rounded">{templatePreview || 'Digite o conteúdo e gere o preview.'}</div>
          </div>
          <div className="grid md:grid-cols-2 gap-3 items-end">
            <div>
              <label className="block text-xs text-muted-foreground mb-1">Telefone para teste</label>
              <Input value={templateTestPhone} onChange={(e) => setTemplateTestPhone(e.target.value)} placeholder="+5511999999999" />
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={handleSendTemplateTest} disabled={!canPreview || !templateTestPhone.trim()}>Enviar Teste</Button>
              <Button onClick={handleSaveTemplate} disabled={loading}>
                {loading ? 'Salvando...' : 'Salvar'}
              </Button>
              <Button variant="outline" onClick={() => setShowTemplateModal(false)}>
                Cancelar
              </Button>
            </div>
          </div>
        </div>
      </Modal>

      {/* Modal de Contato */}
      <Modal
        isOpen={showContactModal}
        onClose={() => setShowContactModal(false)}
        title={selectedItem ? 'Editar Contato' : 'Novo Contato'}
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Nome</label>
            <Input
              value={contactForm.name || ''}
              onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
              placeholder="Nome do contato"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Telefone</label>
            <Input
              value={contactForm.phone || ''}
              onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
              placeholder="+5511999999999"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Email</label>
            <Input
              type="email"
              value={contactForm.email || ''}
              onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
              placeholder="email@exemplo.com"
            />
          </div>
          <div className="flex gap-2">
            <Button onClick={handleSaveContact} disabled={loading}>
              {loading ? 'Salvando...' : 'Salvar'}
            </Button>
            <Button variant="outline" onClick={() => setShowContactModal(false)}>
              Cancelar
            </Button>
          </div>
        </div>
      </Modal>

      {/* Modal de Etiqueta */}
      <Modal
        isOpen={showTagModal}
        onClose={() => setShowTagModal(false)}
        title={selectedItem ? 'Editar Etiqueta' : 'Nova Etiqueta'}
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Nome</label>
            <Input
              value={tagForm.name || ''}
              onChange={(e) => setTagForm({ ...tagForm, name: e.target.value })}
              placeholder="Nome da etiqueta"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Cor</label>
            <Input
              type="color"
              value={tagForm.color || '#3b82f6'}
              onChange={(e) => setTagForm({ ...tagForm, color: e.target.value })}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Descrição</label>
            <Input
              value={tagForm.description || ''}
              onChange={(e) => setTagForm({ ...tagForm, description: e.target.value })}
              placeholder="Descrição da etiqueta"
            />
          </div>
          <div className="flex gap-2">
            <Button onClick={handleSaveTag} disabled={loading}>
              {loading ? 'Salvando...' : 'Salvar'}
            </Button>
            <Button variant="outline" onClick={() => setShowTagModal(false)}>
              Cancelar
            </Button>
          </div>
        </div>
      </Modal>

      {/* Modal de Usuário */}
      <Modal
        isOpen={showUserModal}
        onClose={() => setShowUserModal(false)}
        title={selectedItem ? 'Editar Atendente' : 'Novo Atendente'}
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Nome</label>
            <Input
              value={userForm.name || ''}
              onChange={(e) => setUserForm({ ...userForm, name: e.target.value })}
              placeholder="Nome do atendente"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Email</label>
            <Input
              type="email"
              value={userForm.email || ''}
              onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
              placeholder="email@exemplo.com"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Telefone</label>
            <Input
              value={userForm.phone || ''}
              onChange={(e) => setUserForm({ ...userForm, phone: e.target.value })}
              placeholder="+5511999999999"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Função</label>
            <select
              className="w-full p-2 border rounded-md"
              value={userForm.role || 'attendant'}
              onChange={(e) => setUserForm({ ...userForm, role: e.target.value as any })}
            >
              <option value="attendant">Atendente</option>
              <option value="supervisor">Supervisor</option>
              <option value="admin">Administrador</option>
            </select>
          </div>
          <div className="flex gap-2">
            <Button onClick={handleSaveUser} disabled={loading}>
              {loading ? 'Salvando...' : 'Salvar'}
            </Button>
            <Button variant="outline" onClick={() => setShowUserModal(false)}>
              Cancelar
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default WhatsAppIntegrationPage;
